import { Component, inject, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { RootState } from '../../core/state/shared/root-state.interface';
import {
  dismissAlert,
  login,
} from '../../core/state/user-session/user-session.actions';
import { first, Observable } from 'rxjs';
import {
  selectAlertMessage,
  selectIsLoading,
} from '../../core/state/user-session/user-session.selectors';
import { MatCard, MatCardContent, MatCardFooter } from '@angular/material/card';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { MatDivider } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { getIsMfaEnabled } from '../../core/state/mfa/mfa.actions';
import {
  selectMfaErrorType,
  selectMfaHasFailed,
  selectMfaIsLoading,
} from '../../core/state/mfa/mfa.selectors';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MfaFailComponent } from '../../components/mfa-fail/mfa-fail.component';
import { ProductCodes } from '../../core/models/user-profile.model';
import { MfaErrors } from '../../core/state/mfa/mfa.state';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  imports: [
    MatCard,
    MatCardContent,
    NgClass,
    MatIcon,
    MatIconButton,
    FormsModule,
    MatFormField,
    MatInput,
    MatSuffix,
    RouterLink,
    MatButton,
    MatDivider,
    MatCardFooter,
    AsyncPipe,
    TranslateModule,
    MatProgressSpinner,
    MfaFailComponent,
  ],
})
export class LoginFormComponent implements OnInit {
  username: string;
  password: string;
  alertMessage$: Observable<{ message: string; hasError: boolean }>;
  isLoading$: Observable<boolean>;
  showPassword = false;
  fragment: string | null = null;
  mfaIsLoading$: Observable<boolean>;
  mfaHasFailed$: Observable<boolean>;
  mfaErrorType$: Observable<MfaErrors>;

  private store = inject(Store<RootState>);
  private activateRoute = inject(ActivatedRoute);

  ngOnInit() {
    this.alertMessage$ = this.store.pipe(select(selectAlertMessage));
    this.isLoading$ = this.store.pipe(select(selectIsLoading));

    this.activateRoute.fragment.pipe(first()).subscribe((f) => {
      this.fragment = f;

      if (this.fragment) {
        // we assume this is like key1=val1&key2=val2
        const params = new URLSearchParams(this.fragment);

        const mfaToken = params.get('mfa_token');
        const productParam = params.get('product_code');
        const originUrl = decodeURIComponent(params.get('origin_url') || '');

        if (mfaToken) {
          this.mfaIsLoading$ = this.store.pipe(select(selectMfaIsLoading));
          this.mfaHasFailed$ = this.store.pipe(select(selectMfaHasFailed));
          this.mfaErrorType$ = this.store.pipe(select(selectMfaErrorType));
          this.store.dispatch(
            getIsMfaEnabled({
              mfaToken,
              productCode: productParam as ProductCodes,
              originUrl,
            }),
          );
        }
      }
    });
  }

  onSubmit() {
    this.store.dispatch(
      login({ username: this.username, password: this.password }),
    );
  }

  toggleVisibility() {
    this.showPassword = !this.showPassword;
  }

  hideAlert() {
    this.store.dispatch(dismissAlert());
  }

  protected readonly MfaErrors = MfaErrors;
}
